/**
 * Site configuration — the single knob every child sibling reads to
 * decide which parent features to show / hide.
 *
 * Resolution order (first hit wins):
 *   1. URL query `?site=<slug>`     — preview mode inside the parent
 *   2. `REACT_APP_SITE_SOURCE` env   — set by the child's own Emergent app
 *   3. Hostname prefix / suffix      — kerala.socialpulse.com → "kerala"
 *   4. Default → "socialpulse"
 *
 * Each site's config carries just enough per-brand overrides
 * (allowed nav items, portal section allowlist, headline palette).
 * Everything else — auth, geographic data, AI, moderation — comes
 * from the parent unchanged.
 */

// Canonical site profiles. Keep the shape tight so every child
// declares the exact same fields — makes it trivial to spin up a new
// sibling: just add a row here.
export const SITE_PROFILES = {
  socialpulse: {
    slug:        "socialpulse",
    label:       "Social Pulse",
    // null hideNav → show every nav item
    hideNav:     null,
    // null portalSections → show every taxonomy section
    portalSections: null,
    defaultLang: "en",
    countryPref: null,
    accent:      "#7C3AED",
  },
  kerala: {
    slug:        "kerala",
    label:       "Kerala Pulse",
    // Kerala emphasises districts + tourism + community; drop the
    // items that are US-only or off-brand.
    hideNav:     ["family_tree", "civic_reports"],
    portalSections: null,
    defaultLang: "ml",
    countryPref: "india",
    accent:      "#0E7C55",
  },
  foodpulse: {
    slug:        "foodpulse",
    label:       "Food Pulse",
    // Food Pulse is a vertical: hide non-food sections at the nav
    // level and restrict Portal View to food-adjacent taxonomy.
    hideNav:     ["family_tree", "memorials", "housing", "sports", "education", "government"],
    portalSections: [
      "food",          // restaurants / markets / bakeries / farmers
      "directory",     // hospitals stays out — only food POIs surface
      "community",     // local food news / marketplace
      "business",      // markets / procurement
      "news-hub",      // food-adjacent news
      "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#EA580C",
  },
  indias: {
    slug:        "indias",
    label:       "Indias",
    // Indias.com is India-only. Hide US-specific directories.
    hideNav:     ["us_counties", "us_states"],
    portalSections: null,
    defaultLang: "hi",
    countryPref: "india",
    accent:      "#B45309",
  },
  keralatravels: {
    slug:        "keralatravels",
    label:       "Kerala Travels",
    // Hotel-booking sibling for Kerala tourism. Hide non-travel
    // sections at nav-level and lock the Portal View to
    // tourism-adjacent taxonomy.
    hideNav:     ["family_tree", "memorials", "housing", "civic_reports",
                  "us_counties", "us_states"],
    portalSections: [
      "tourism", "travel", "food", "directory",
      "business", "community", "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: "india",
    accent:      "#0E7C55",
  },
  indianfamilytree: {
    slug:        "indianfamilytree",
    label:       "Indian Family Tree",
    // Genealogy-focused sibling. Hide domain-irrelevant nav so the
    // homepage reads as a family-tree destination, not a general
    // portal. Family Tree stays front-and-centre.
    hideNav:     ["food_pulse", "civic_reports", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace", "sports"],
    portalSections: [
      "family", "memorials", "community", "directory",
      "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: "india",
    accent:      "#B25D38", // saffron
  },
  myfamilytreecare: {
    slug:        "myfamilytreecare",
    label:       "MyFamilyTree.care",
    // Heritage-archive sibling. Same nav footprint as IFT — this is a
    // global memorial/heritage brand. Data still lives in the shared
    // Social Pulse DB; child-site attribution is written via the
    // ``X-Site-Source`` header on every write.
    hideNav:     ["food_pulse", "civic_reports", "housing", "us_counties",
                  "us_states", "real_estate", "marketplace", "sports"],
    portalSections: [
      "family", "memorials", "community", "directory",
      "news-hub", "contact",
    ],
    defaultLang: "en",
    countryPref: null,
    accent:      "#0E7C55", // heritage green
  },
};

// Hostname → slug overrides that don't follow the `<slug>.com`
// pattern (custom domains, staging URLs, etc.). Wins over the
// prefix/suffix heuristics below.
const HOST_ALIASES = {
  "hotel-booking-beta.emergent.host": "keralatravels",
  // The `.care` TLD needs an explicit alias — the prefix/suffix
  // heuristic below only matches `<slug>.com`.
  "myfamilytree.care":       "myfamilytreecare",
  "www.myfamilytree.care":   "myfamilytreecare",
  // FoodPulse is deployed on the `.net` apex — the `<slug>.com`
  // heuristic misses it, so we alias explicitly.
  "foodpulse.net":       "foodpulse",
  "www.foodpulse.net":   "foodpulse",
  // Indian Family Tree matches via the .com heuristic already; the
  // www. variant just needs the explicit alias to skip the heuristic.
  "www.indianfamilytree.com":  "indianfamilytree",
  // Preview hosts used for QA prior to production deploys.
  "foodpulse.preview.emergent.host":       "foodpulse",
  "indianfamilytree.preview.emergent.host": "indianfamilytree",
};

const ALLOWED = new Set(Object.keys(SITE_PROFILES));

function fromHostname(host) {
  const h = (host || "").toLowerCase();
  if (HOST_ALIASES[h]) return HOST_ALIASES[h];
  for (const slug of ALLOWED) {
    // Prefix (kerala.socialpulse.com) or apex (foodpulse.net,
    // indias.com) both count. `socialpulse.com` itself falls
    // through to the default profile.
    if (slug === "socialpulse") continue;
    if (h.startsWith(`${slug}.`)) return slug;
    if (h === `${slug}.com` || h.endsWith(`.${slug}.com`)) return slug;
  }
  return null;
}

/** Resolve the active site slug at render time. */
export function resolveSiteSlug({ queryParam, hostname } = {}) {
  const q = (queryParam || "").toLowerCase();
  if (q && ALLOWED.has(q)) return q;

  const envSite = (process.env.REACT_APP_SITE_SOURCE || "").toLowerCase();
  if (envSite && ALLOWED.has(envSite)) return envSite;

  const fromHost = fromHostname(hostname || (typeof window !== "undefined" ? window.location.hostname : ""));
  if (fromHost) return fromHost;

  return "socialpulse";
}

/** Full profile for a site slug — always returns a non-null profile. */
export function siteProfile(slug) {
  return SITE_PROFILES[(slug || "").toLowerCase()] || SITE_PROFILES.socialpulse;
}

/** Convenience predicates used across the nav/portal machinery. */
export function isNavHidden(profile, key) {
  return Array.isArray(profile?.hideNav) && profile.hideNav.includes(key);
}

export function isPortalSectionAllowed(profile, sectionSlug) {
  if (!profile?.portalSections) return true;
  return profile.portalSections.includes(sectionSlug);
}
